import buildCanvasQueryKey from '@/hooks/canvas/helpers/buildCanvasQueryKey';
import invalidateCanvasGraph from '@/hooks/canvas/helpers/invalidateCanvasGraph';
import type { SerializedCanvasItem } from '@/hooks/canvas/types';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import type {
    BuildRefetchCanvasOnItemDescriptionUpdateSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_ITEM_DESCRIPTION_UPDATE_TOOL_NAMES = [
    'update_canvas_item_description',
    'canvases.updateItemDescription',
];

const buildRefetchCanvasOnItemDescriptionUpdateSubscription = (
    _params: BuildRefetchCanvasOnItemDescriptionUpdateSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-item-description-update',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_ITEM_DESCRIPTION_UPDATE_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const canvasIdFromArgs = getStringValue({ value: args.canvasId });

            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ event, queryClient, matchParams }) => {
            const args = coerceToRecord({ value: event.args });
            const itemId = getStringValue({ value: args.itemId });
            const description = getStringValue({ value: args.description });

            if (itemId) {
                queryClient.setQueryData(
                    buildCanvasQueryKey({ scope: 'items', canvasId: matchParams.canvasId }),
                    (existingItems: SerializedCanvasItem[] | undefined) => {
                        const safeExistingItems = existingItems ?? [];
                        return safeExistingItems.map(existingItem => {
                            if (existingItem.itemId !== itemId) {
                                return existingItem;
                            }
                            return {
                                ...existingItem,
                                description,
                            };
                        });
                    }
                );
            }

            invalidateCanvasGraph({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnItemDescriptionUpdateSubscription;
